import { log } from "../../utils";
import type { PageBase } from "../capture/base";
import { Mutations } from "./observer";

export class Animate {
  public enabled = false;

  constructor(private page: PageBase) {}

  private watchList = new Map<
    Animation,
    { state: AnimationPlayState; properties: string[] }
  >();

  private unhook = () => {};

  public track() {
    try {
      this.untrack();

      const self = this;
      const proto = this.page.getWindow().Element.prototype;
      const originalAnimate = proto.animate;

      proto.animate = function (...args: any) {
        const animation = originalAnimate.apply(this, args);

        if (self.enabled) {
          setTimeout(() => {
            self.watch(animation);
          }, 0);
        }

        return animation;
      };

      this.unhook = () => {
        this.unhook = () => {};
        this.watchList.clear();
        proto.animate = originalAnimate;
      };

      this.enabled = true;
    } catch (ex) {
      log(ex);
    }
  }

  public untrack() {
    this.unhook();
    this.enabled = false;
  }

  public add(target: Node) {
    const anims = (target.getRootNode() as Element).getAnimations?.();

    if (anims)
      for (const animation of anims) {
        this.watch(animation);
      }
  }

  public watch(animation: Animation) {
    const properties = (animation.effect as KeyframeEffect)
      ?.getKeyframes()
      ?.reduce((acc, kf) => ({ ...acc, ...kf }), {});

    if (properties) {
      this.watchList.set(animation, {
        state: null,
        properties: Object.keys(properties),
      });
    }
  }

  public flush() {
    const changes: Mutations = { styles: {} };
    let hasChanges = false;

    this.watchList.forEach((details, animation) => {
      try {
        if (animation.playState === details.state) {
          if (animation.playState === "idle") {
            this.watchList.delete(animation);
          }

          return;
        }

        details.state = animation.playState;

        const target = (animation.effect as KeyframeEffect)?.target;
        const targetId = this.page.idp.getId(target);

        if (!targetId) return;

        const styles = getComputedStyle(target);
        changes.styles[targetId] ??= {};

        for (const property of details.properties) {
          const value = styles.getPropertyValue(property);

          if (value) {
            changes.styles[targetId][property] = value;
            hasChanges = true;
          }
        }
      } catch (error) {
        log(error);
      }
    });

    return hasChanges ? changes : null;
  }
}
